import React, { useState } from 'react';

import { useBaseTable } from 'hooks/useBaseTable';
import { useBaseWidget } from 'hooks/useBaseWidget';

import { ITopOverdueData } from 'api/mutations/extractDelayDriverCsv.mutation';

import { TooltipPosition } from 'components/common/CustomizedTooltip/customizedTooltip.enums';
import ErrorBoundary from 'components/common/ErrorBoundary/ErrorBoundary';
import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import Icon from 'components/common/Icons/icon';
import { CustomTableColumnsModal } from 'components/common/Modals/CustomTableColumns/customTableColumnsModal';
import NoData from 'components/common/NoData/noData';
import { ShareInsightButton } from 'components/common/ShareInsightButton/ShareInsightButton';
import { TableV8 } from 'components/common/Tables/TableV8/tableV8';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';
import { CustomPagination } from 'components/common/pagination/customPagination';

import classes from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/style/table.module.scss';
import { AddWidget } from 'components/Dashboards/Project/Components/CustomDashboard/AddRemoveWidget/addWidget';
import { PinboardWidgetPills } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetPills/PinboardWidgetPills';
import { IRenderCriticalAnalysisDelayDrivers } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { CustomizedRoundIcon, CustomizedTooltip } from 'components/common';
import { useFinishDateFuturePotentialDelayDriversLogic } from 'components/widgets/FinishDateFuturePotentialDelayDrivers/components/FinishDateFuturePotentialDelayDrivers.logic';
import { useFinishDateFuturePotentialDelayDriversProvider } from 'components/widgets/FinishDateFuturePotentialDelayDrivers/components/FinishDateFuturePotentialDelayDrivers.provider';
import {
    columnsV8,
    initialState,
} from 'components/widgets/FinishDateFuturePotentialDelayDrivers/components/FinishDateFuturePotentialDelayDrivers.utils';
import { CriticalAnalysisDelayDriversFilter } from 'components/widgets/FinishDateFuturePotentialDelayDrivers/components/FinishDateFuturePotentialDelayDriversFilter';

const title = 'Critical Activities & Delay Drivers (Risk Lookahead)';
const id = 'futurePotentialDelayDrivers';
const componentKey = 'finishDateFuturePotentialDelayDrivers';

const FinishDateFuturePotentialDelayDrivers = ({
    projectId,
    contractId,
    widgetId,
    latestVersionId,
    localFilters,
    globalFilterData,
    externalComponents = null,
    editNarrative = null,
    setSubTitleForExport,
}: IRenderCriticalAnalysisDelayDrivers) => {
    const { route, subTitle } = useBaseWidget({
        projectId,
        contractId,
        latestVersionId,
        title,
        setSubTitleForExport,
        id,
        widgetId,
    });

    const { table } = useBaseTable({
        widgetId,
        localFilters,
        componentKey,
        initialState,
        columns: columnsV8,
    });

    const { ui, api } = useFinishDateFuturePotentialDelayDriversProvider({
        widgetId,
        localFilters,
        projectId,
        contractId,
        globalFilterData,
        latestVersionId,
        table,
    });

    const { logic } = useFinishDateFuturePotentialDelayDriversLogic({
        api,
        ui,
        table,
        title,
        subTitle,
    });

    return (
        <ErrorBoundary>
            <WidgetWithTitle
                id={id}
                title={title}
                tooltip={api.getActivities.data?.info}
                titleFilters={[
                    editNarrative && <div key={'editNarrative'}>{editNarrative}</div>,
                    widgetId && ui.pills.length > 0 ? (
                        <PinboardWidgetPills key={'widgetFilters'} widgetId={widgetId} filters={ui.pills} />
                    ) : (
                        <CriticalAnalysisDelayDriversFilter
                            key={'filter'}
                            handleOnSearch={logic.handleOnSearch}
                            dateFilter={ui.state.dateFilter}
                            handleDateFilter={logic.handleDate}
                            months={logic.months}
                            handleStatus={logic.handleStatus}
                            handleStatusType={logic.handleStatusType}
                            statusFilter={ui.state.statusFilter}
                            typeFilter={ui.state.typeFilter}
                            handleCriticalPath={logic.handleCriticalPath}
                            isCriticalPath={ui.state.isCriticalPath}
                            handleDelayDriver={logic.handleDelayDriver}
                            isDelayDriver={ui.state.isDelayDriver}
                        />
                    ),
                ]}
                titleComponents={[
                    externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                    <CustomizedTooltip
                        key={'CustomizTableColumns'}
                        tooltipPosition={TooltipPosition.Top}
                        tooltipContent={'Customize table columns'}
                        triggerElement={
                            <CustomizedRoundIcon enableHover={true} onClick={() => table.setModalColumnsOpen(true)}>
                                <Icon name={'tune-filter'} />
                            </CustomizedRoundIcon>
                        }
                    />,
                    <AddWidget
                        key={'AddWidget'}
                        componentKey={componentKey}
                        title={title}
                        projectId={projectId}
                        contractId={contractId}
                        widgetId={widgetId}
                        route={route}
                        localFilters={ui.state}
                        callBack={table.setWidgetColumns}
                    />,
                    <ShareInsightButton key={'shareInsight'} title={title} link={route} />,
                    <ExportDropdown
                        key={'export'}
                        title={title}
                        handleAPICsvExport={logic.handleAPICsvExport}
                        subTitle={subTitle}
                    />,
                ]}
            >
                <div data-testid={'table-container'} className={classes.tableContainer}>
                    {table.updatedInitialState && (
                        <>
                            <TableV8<ITopOverdueData>
                                data={logic.filteredRows}
                                columns={columnsV8}
                                isLoading={api.getActivities.isLoading || api.getActivities.isFetching}
                                noData={<NoData>No activities match the applied filter</NoData>}
                                sorting={table.sorting}
                                setSorting={table.setSorting}
                                columnVisibility={table.columnVisibility}
                                setColumnVisibility={table.setColumnVisibility}
                                columnOrder={table.columnOrder}
                                setColumnOrder={table.setColumnOrder}
                                rowSelection={table.rowSelection}
                                setRowSelection={table.setRowSelection}
                            />

                            <div className={classes.pagination}>
                                <CustomPagination
                                    totalPages={table.totalPages}
                                    pagination={table.pagination}
                                    setPagination={table.setPagination}
                                    isDisabled={api.getActivities.isFetching}
                                />
                            </div>
                        </>
                    )}
                </div>

                <CustomTableColumnsModal
                    onHideModal={() => table.setModalColumnsOpen(false)}
                    showModal={table.modalColumnsOpen}
                    widgetId={widgetId}
                    componentKey={componentKey}
                    tableInstance={table.tableInstance}
                    initialState={initialState}
                    columns={columnsV8}
                    onChangeOrder={table.setColumnOrder}
                    onChangeVisibility={table.setColumnVisibility}
                />
            </WidgetWithTitle>
        </ErrorBoundary>
    );
};

export default React.memo(FinishDateFuturePotentialDelayDrivers);
